/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Users, Mail, Send, Upload, Search, Filter, X, ChevronRight,
  ChevronDown, ChevronUp, Edit2, Trash2, Plus, Check, Copy,
  MessageSquare, DollarSign, ExternalLink, Clock, RefreshCw,
  Instagram, Eye, FileText, Move, Play, Pause, CheckCircle,
  Zap, Globe, AlertCircle, Loader, Square, CheckSquare, Download,
  Settings
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://vaulto.social/api';

// Pipeline stages config
const STAGES = [
  { id: 'lead', label: 'Lead', color: 'bg-gray-100 text-gray-800' },
  { id: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-800' },
  { id: 'negotiating', label: 'Negotiating', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'active_partner', label: 'Active Partner', color: 'bg-green-100 text-green-800' }
];

// Template categories
const TEMPLATE_CATEGORIES = [
  { id: 'cold_outreach', label: 'Cold Outreach' },
  { id: 'followup', label: 'Follow-up' },
  { id: 'deal_proposal', label: 'Deal Proposal' },
  { id: 'partnership_welcome', label: 'Welcome' }
];

// Deal types
const DEAL_TYPES = [
  { id: 'flat_fee', label: 'Flat Fee per Conversion' },
  { id: 'commission_percent', label: 'Commission %' },
  { id: 'revenue_share', label: 'Revenue Share %' },
  { id: 'hybrid', label: 'Hybrid Deal' }
];

// Drip status config
const DRIP_STATUSES = {
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-600', icon: Clock },
  in_sequence: { label: 'In Sequence', color: 'bg-blue-100 text-blue-700', icon: Play },
  replied: { label: 'Replied', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-purple-100 text-purple-700', icon: Check },
  stopped: { label: 'Stopped', color: 'bg-yellow-100 text-yellow-700', icon: Pause }
};

// Format follower count
const formatFollowers = (count) => {
  if (!count) return '—';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

// Get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { headers: { 'Authorization': `Bearer ${token}` } };
};

// ================== MAIN COMPONENT ==================

export default function InfluencerOutreach() {
  // View state
  const [viewMode, setViewMode] = useState('kanban'); // kanban, table
  const [subTab, setSubTab] = useState('pipeline'); // pipeline, templates
  
  // Data state
  const [influencers, setInfluencers] = useState([]);
  const [kanbanData, setKanbanData] = useState({});
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    stage: '',
    niche: '',
    has_email: '',
    search: ''
  });
  
  // Modals
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showDMComposer, setShowDMComposer] = useState(false);
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(false);
  
  // Drip & DM state
  const [dripStatus, setDripStatus] = useState(null);
  const [dmTemplates, setDmTemplates] = useState([]);
  const [selectedDMTemplate, setSelectedDMTemplate] = useState(null);
  const [preparedDM, setPreparedDM] = useState(null);
  
  // Discovery state (legacy)
  const [discoveryInProgress, setDiscoveryInProgress] = useState(false);
  const [discoveryResults, setDiscoveryResults] = useState(null);
  const [discoveryEmailOnly, setDiscoveryEmailOnly] = useState(false);
  const [discoveryNiche, setDiscoveryNiche] = useState('instagram growth');
  const [customNiche, setCustomNiche] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [importingLeads, setImportingLeads] = useState(false);
  const [showDeclinedModal, setShowDeclinedModal] = useState(false);
  const [declinedLeads, setDeclinedLeads] = useState([]);
  
  // Prospects state (background discovery)
  const [showProspectsModal, setShowProspectsModal] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [prospectsLoading, setProspectsLoading] = useState(false);
  const [prospectsStats, setProspectsStats] = useState(null);
  const [prospectFilter, setProspectFilter] = useState({ hasEmail: null, minFollowers: null });
  const [currentProspectIndex, setCurrentProspectIndex] = useState(0);
  
  // Niche configuration state
  const [showNicheConfig, setShowNicheConfig] = useState(false);
  const [configuredNiches, setConfiguredNiches] = useState([]);
  const [newNiche, setNewNiche] = useState('');
  const [newNicheEmailOnly, setNewNicheEmailOnly] = useState(false);
  
  // Email preview state
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Predefined niches
  const DISCOVERY_NICHES = [
    'instagram growth',
    'instagram marketing',
    'social media coach',
    'instagram tips',
    'content creator',
    'custom'
  ];
  
  // Selected items
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Form data
  const [newInfluencer, setNewInfluencer] = useState({
    instagram_handle: '', email: '', full_name: '', niche: '', follower_count: '', notes: ''
  });
  const [emailData, setEmailData] = useState({
    template_id: null, subject: '', html_content: '', text_content: '', custom_offer: ''
  });
  const [dealData, setDealData] = useState({
    deal_type: 'flat_fee', flat_fee_amount: '', commission_percent: '', 
    revshare_percent: '', revshare_months: '', custom_terms: '', activate_now: true
  });
  const [csvData, setCsvData] = useState({ content: '', preview: null, importMode: 'file' });
  const [csvFile, setCsvFile] = useState(null);
  const [csvDragActive, setCsvDragActive] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '', category: 'cold_outreach', subject: '', html_content: '', text_content: '', description: ''
  });

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailTemplateId, setBulkEmailTemplateId] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState('created');
  const [sortDir, setSortDir] = useState('desc');

  // Enrichment
  const [enrichTaskId, setEnrichTaskId] = useState(null);
  const [enriching, setEnriching] = useState(false);

  // Quick outreach
  const [showQuickOutreachModal, setShowQuickOutreachModal] = useState(false);
  const [quickOutreachTemplateId, setQuickOutreachTemplateId] = useState(null);

  // Outreach automation (Smartlead pipeline)
  const [automationStats, setAutomationStats] = useState(null);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [showAdvancedManual, setShowAdvancedManual] = useState(false);
  const [needsReviewList, setNeedsReviewList] = useState([]);
  const [showNeedsReview, setShowNeedsReview] = useState(false);

  // ================== DATA FETCHING ==================

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencers/stats`, getAuthHeaders());
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchAutomationStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencers/automation/stats`, getAuthHeaders());
      setAutomationStats(res.data);
    } catch (err) {
      console.error('Error fetching automation stats:', err);
    }
  }, []);

  const fetchNeedsReview = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencers/automation/needs-review`, getAuthHeaders());
      setNeedsReviewList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching needs-review list:', err);
    }
  }, []);

  const handleToggleAutomationPause = async () => {
    if (!automationStats) return;
    setAutomationLoading(true);
    try {
      const newPaused = !automationStats.settings.is_paused;
      await axios.patch(
        `${API_URL}/admin/influencers/automation/settings`,
        { is_paused: newPaused },
        getAuthHeaders()
      );
      toast.success(newPaused ? 'Automation paused' : 'Automation resumed');
      fetchAutomationStats();
    } catch (err) {
      toast.error('Failed to update automation settings');
    } finally {
      setAutomationLoading(false);
    }
  };

  const handleUpdateAutomationCap = async (newCap) => {
    setAutomationLoading(true);
    try {
      await axios.patch(
        `${API_URL}/admin/influencers/automation/settings`,
        { daily_enrollment_cap: newCap },
        getAuthHeaders()
      );
      toast.success(`Daily cap updated to ${newCap}`);
      fetchAutomationStats();
    } catch (err) {
      toast.error('Failed to update cap');
    } finally {
      setAutomationLoading(false);
    }
  };

  const handleRunEnrollmentNow = async () => {
    setAutomationLoading(true);
    try {
      await axios.post(`${API_URL}/admin/influencers/automation/run-enrollment-now`, {}, getAuthHeaders());
      toast.success('Enrollment task started');
      setTimeout(fetchAutomationStats, 4000);
    } catch (err) {
      toast.error('Failed to start enrollment');
    } finally {
      setAutomationLoading(false);
    }
  };

  const handleClearNeedsReview = async (id) => {
    try {
      await axios.post(`${API_URL}/admin/influencers/automation/needs-review/${id}/clear`, {}, getAuthHeaders());
      fetchNeedsReview();
      fetchAutomationStats();
    } catch (err) {
      toast.error('Failed to clear flag');
    }
  };

  const fetchInfluencers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.niche) params.append('niche', filters.niche);
      if (filters.has_email) params.append('has_email', filters.has_email === 'yes');
      if (filters.search) params.append('search', filters.search);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortDir) params.append('sort_dir', sortDir);

      const res = await axios.get(`${API_URL}/admin/influencers?${params}`, getAuthHeaders());
      setInfluencers(res.data.influencers);
    } catch (err) {
      console.error('Error fetching influencers:', err);
      toast.error('Failed to load influencers');
    }
  }, [filters, sortBy, sortDir]);

  const fetchKanban = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencers/kanban`, getAuthHeaders());
      setKanbanData(res.data);
    } catch (err) {
      console.error('Error fetching kanban:', err);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencer-templates`, getAuthHeaders());
      setTemplates(res.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, []);

  const fetchInfluencerDetail = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencers/${id}`, getAuthHeaders());
      setSelectedInfluencer(res.data);
      // Also fetch drip status
      fetchDripStatus(id);
    } catch (err) {
      console.error('Error fetching influencer:', err);
      toast.error('Failed to load influencer details');
    }
  };
  
  const fetchDripStatus = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencers/${id}/drip`, getAuthHeaders());
      setDripStatus(res.data);
    } catch (err) {
      console.error('Error fetching drip status:', err);
    }
  };
  
  const fetchDMTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencer-dm-templates`, getAuthHeaders());
      setDmTemplates(res.data);
    } catch (err) {
      console.error('Error fetching DM templates:', err);
    }
  }, []);
  
  // Fetch pending prospects for review
  const fetchProspects = async () => {
    setProspectsLoading(true);
    try {
      const params = new URLSearchParams({ status: 'pending', limit: '100' });
      if (prospectFilter.hasEmail) params.append('has_email', 'true');
      if (prospectFilter.minFollowers) params.append('min_followers', prospectFilter.minFollowers);
      
      const res = await axios.get(`${API_URL}/admin/influencers/prospects?${params}`, getAuthHeaders());
      setProspects(res.data.prospects || []);
      setCurrentProspectIndex(0);
    } catch (err) {
      console.error('Error fetching prospects:', err);
      toast.error('Failed to load prospects');
    } finally {
      setProspectsLoading(false);
    }
  };
  
  // Fetch prospects stats
  const fetchProspectsStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencers/prospects/stats`, getAuthHeaders());
      setProspectsStats(res.data);
    } catch (err) {
      console.error('Error fetching prospects stats:', err);
    }
  };
  
  // Fetch configured niches
  const fetchNiches = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencers/discovery/niches`, getAuthHeaders());
      setConfiguredNiches(res.data.niches || []);
    } catch (err) {
      console.error('Error fetching niches:', err);
    }
  };
  
  // Add new niche
  const handleAddNiche = async () => {
    if (!newNiche.trim()) return;
    try {
      await axios.post(`${API_URL}/admin/influencers/discovery/niches`, { 
        niche: newNiche.trim(),
        email_only: newNicheEmailOnly
      }, getAuthHeaders());
      toast.success(`Niche added${newNicheEmailOnly ? ' (email only)' : ''}`);
      setNewNiche('');
      setNewNicheEmailOnly(false);
      fetchNiches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add niche');
    }
  };
  
  // Toggle niche active status
  const handleToggleNiche = async (nicheId, isActive) => {
    try {
      await axios.patch(`${API_URL}/admin/influencers/discovery/niches/${nicheId}`, { is_active: !isActive }, getAuthHeaders());
      fetchNiches();
    } catch (err) {
      toast.error('Failed to update niche');
    }
  };
  
  // Delete niche
  const handleDeleteNiche = async (nicheId) => {
    try {
      await axios.delete(`${API_URL}/admin/influencers/discovery/niches/${nicheId}`, getAuthHeaders());
      fetchNiches();
    } catch (err) {
      toast.error('Failed to delete niche');
    }
  };
  
  // Preview email content
  const handlePreviewEmail = async (outreachId) => {
    setLoadingPreview(true);
    try {
      const response = await axios.get(`${API_URL}/admin/influencers/outreach/${outreachId}`, getAuthHeaders());
      setPreviewEmail(response.data);
      setShowEmailPreview(true);
    } catch (err) {
      toast.error('Failed to load email preview');
    } finally {
      setLoadingPreview(false);
    }
  };
  
  // Accept prospect (add to pipeline)
  const handleAcceptProspect = async (prospect) => {
    try {
      await axios.post(`${API_URL}/admin/influencers/import-leads`, {
        leads: [{
          username: prospect.username,
          full_name: prospect.full_name,
          email: prospect.email,
          follower_count: prospect.follower_count,
          bio: prospect.bio,
          website: prospect.website
        }]
      }, getAuthHeaders());
      
      toast.success(`@${prospect.username} added to pipeline!`);
      
      // Move to next prospect
      setProspects(prev => prev.filter(p => p.id !== prospect.id));
      fetchProspectsStats();
      fetchInfluencers();
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error('Failed to accept prospect');
    }
  };
  
  // Decline prospect
  const handleDeclineProspect = async (prospect) => {
    try {
      await axios.post(`${API_URL}/admin/influencers/decline-lead`, {
        username: prospect.username
      }, getAuthHeaders());
      
      toast.info(`@${prospect.username} declined`);
      
      // Move to next prospect
      setProspects(prev => prev.filter(p => p.id !== prospect.id));
      fetchProspectsStats();
    } catch (err) {
      toast.error('Failed to decline prospect');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchInfluencers(),
        fetchKanban(),
        fetchTemplates(),
        fetchDMTemplates(),
        fetchAutomationStats(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchInfluencers, fetchKanban, fetchTemplates, fetchDMTemplates, fetchAutomationStats]);

  // Refresh automation stats every 60 seconds while the page is open.
  useEffect(() => {
    const id = setInterval(fetchAutomationStats, 60000);
    return () => clearInterval(id);
  }, [fetchAutomationStats]);

  // ================== ACTIONS ==================

  const handleAddInfluencer = async () => {
    try {
      await axios.post(`${API_URL}/admin/influencers`, newInfluencer, getAuthHeaders());
      toast.success('Influencer added successfully');
      setShowAddModal(false);
      setNewInfluencer({ instagram_handle: '', email: '', full_name: '', niche: '', follower_count: '', notes: '' });
      fetchInfluencers();
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add influencer');
    }
  };

  const handleUpdateStage = async (influencerId, newStage) => {
    try {
      await axios.put(`${API_URL}/admin/influencers/${influencerId}`, { stage: newStage }, getAuthHeaders());
      fetchKanban();
      fetchStats();
      if (selectedInfluencer?.id === influencerId) {
        fetchInfluencerDetail(influencerId);
      }
    } catch (err) {
      toast.error('Failed to update stage');
    }
  };

  const handleDeleteInfluencer = async (id) => {
    if (!window.confirm('Are you sure you want to delete this influencer?')) return;
    try {
      await axios.delete(`${API_URL}/admin/influencers/${id}`, getAuthHeaders());
      toast.success('Influencer deleted');
      setShowDetailDrawer(false);
      setSelectedInfluencer(null);
      fetchInfluencers();
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete influencer');
    }
  };

  const handleCsvFileSelect = (file) => {
    if (!file) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setCsvData({ ...csvData, content: e.target.result, preview: null });
    };
    reader.readAsText(file);
  };

  const handleCsvDrop = (e) => {
    e.preventDefault();
    setCsvDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.txt') || file.name.endsWith('.tsv'))) {
      handleCsvFileSelect(file);
    } else {
      toast.error('Please drop a .csv, .txt, or .tsv file');
    }
  };

  const handlePreviewImport = async () => {
    if (!csvData.content.trim()) return;
    setImportLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/influencers/import/preview`, 
        { csv_content: csvData.content }, getAuthHeaders());
      setCsvData({ ...csvData, preview: res.data });
    } catch (err) {
      toast.error('Failed to parse CSV: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setImportLoading(false);
    }
  };

  const handleImport = async () => {
    setImportLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/influencers/import`, 
        { csv_content: csvData.content }, getAuthHeaders());
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported} influencers. ${res.data.skipped} skipped.`);
      fetchInfluencers();
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error('Import failed: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setImportLoading(false);
    }
  };

  const handleEnrich = async (ids) => {
    setEnriching(true);
    try {
      const res = await axios.post(`${API_URL}/admin/influencers/enrich`, 
        { influencer_ids: ids }, getAuthHeaders());
      setEnrichTaskId(res.data.task_id);
      toast.success(`Enriching ${ids.length} profiles in background...`);
      pollEnrichStatus(res.data.task_id);
    } catch (err) {
      toast.error('Failed to start enrichment');
      setEnriching(false);
    }
  };

  const pollEnrichStatus = async (taskId) => {
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/influencers/enrich/status/${taskId}`, getAuthHeaders());
        if (res.data.status === 'completed') {
          const r = res.data.result;
          toast.success(`Enriched ${r.enriched} profiles (${r.failed} failed)`);
          setEnriching(false);
          setEnrichTaskId(null);
          fetchInfluencers();
          fetchKanban();
          return;
        } else if (res.data.status === 'failed') {
          toast.error('Enrichment failed');
          setEnriching(false);
          setEnrichTaskId(null);
          return;
        }
        setTimeout(poll, 3000);
      } catch {
        setEnriching(false);
        setEnrichTaskId(null);
      }
    };
    setTimeout(poll, 3000);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setCsvData({ content: '', preview: null, importMode: 'file' });
    setCsvFile(null);
    setImportResult(null);
  };

  // Bulk actions
  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === influencers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(influencers.map(i => i.id)));
    }
  };

  const handleBulkEmail = async () => {
    if (!bulkEmailTemplateId) { toast.error('Select a template'); return; }
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/influencers/bulk-email`, {
        influencer_ids: Array.from(selectedIds),
        template_id: bulkEmailTemplateId
      }, getAuthHeaders());
      toast.success(`Sent ${res.data.sent} emails (${res.data.failed} failed, ${res.data.skipped_no_email} had no email)`);
      setShowBulkEmailModal(false);
      setSelectedIds(new Set());
      fetchInfluencers();
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error('Bulk email failed: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDripStart = async () => {
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/influencers/bulk-drip-start`, {
        influencer_ids: Array.from(selectedIds)
      }, getAuthHeaders());
      toast.success(`Started drip for ${res.data.started} influencers (${res.data.skipped} skipped)`);
      setSelectedIds(new Set());
      fetchInfluencers();
      fetchKanban();
    } catch (err) {
      toast.error('Bulk drip start failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} influencer(s)? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/influencers/bulk-delete`, {
        influencer_ids: Array.from(selectedIds)
      }, getAuthHeaders());
      toast.success(`Deleted ${res.data.deleted} influencers`);
      setSelectedIds(new Set());
      fetchInfluencers();
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error('Bulk delete failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkStageUpdate = async (stage) => {
    setBulkLoading(true);
    try {
      await axios.post(`${API_URL}/admin/influencers/bulk-stage-update`, {
        influencer_ids: Array.from(selectedIds),
        stage
      }, getAuthHeaders());
      toast.success(`Updated ${selectedIds.size} influencers to ${stage}`);
      setSelectedIds(new Set());
      fetchInfluencers();
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error('Bulk stage update failed');
    } finally {
      setBulkLoading(false);
    }
  };

  // Quick outreach
  const handleQuickOutreach = async () => {
    if (!quickOutreachTemplateId) { toast.error('Select a template'); return; }
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/influencers/quick-outreach`, {
        template_id: quickOutreachTemplateId,
        stage: 'lead'
      }, getAuthHeaders());
      toast.success(res.data.message);
      setShowQuickOutreachModal(false);
      fetchInfluencers();
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error('Quick outreach failed');
    } finally {
      setBulkLoading(false);
    }
  };

  // Drip sequence actions
  const handleStartDrip = async () => {
    try {
      await axios.post(`${API_URL}/admin/influencers/${selectedInfluencer.id}/drip/start`, {}, getAuthHeaders());
      toast.success('Drip sequence started');
      fetchDripStatus(selectedInfluencer.id);
      fetchInfluencerDetail(selectedInfluencer.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start drip sequence');
    }
  };
  
  const handleStopDrip = async () => {
    try {
      await axios.post(`${API_URL}/admin/influencers/${selectedInfluencer.id}/drip/stop`, {}, getAuthHeaders());
      toast.success('Drip sequence stopped');
      fetchDripStatus(selectedInfluencer.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to stop drip sequence');
    }
  };
  
  const handleMarkReplied = async () => {
    try {
      await axios.post(`${API_URL}/admin/influencers/${selectedInfluencer.id}/drip/replied`, {}, getAuthHeaders());
      toast.success('Marked as replied - moved to Negotiating');
      fetchDripStatus(selectedInfluencer.id);
      fetchInfluencerDetail(selectedInfluencer.id);
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as replied');
    }
  };
  
  // DM preparation
  const handlePrepareDM = async (templateId = null) => {
    try {
      const url = templateId 
        ? `${API_URL}/admin/influencers/${selectedInfluencer.id}/dm/prepare?template_id=${templateId}`
        : `${API_URL}/admin/influencers/${selectedInfluencer.id}/dm/prepare`;
      const res = await axios.get(url, getAuthHeaders());
      setPreparedDM(res.data);
      setShowDMComposer(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to prepare DM');
    }
  };
  
  const handleLogDMSent = async () => {
    try {
      await axios.post(`${API_URL}/admin/influencers/${selectedInfluencer.id}/dm/log`, {
        template_id: preparedDM?.template_id,
        content: preparedDM?.content
      }, getAuthHeaders());
      toast.success('DM logged successfully');
      setShowDMComposer(false);
      setPreparedDM(null);
      fetchInfluencerDetail(selectedInfluencer.id);
    } catch (err) {
      toast.error('Failed to log DM');
    }
  };
  
  // Lead discovery
  const handleDiscoverLeads = async (emailOnly = false) => {
    // Determine which niche to search
    const nicheToSearch = discoveryNiche === 'custom' ? customNiche.trim() : discoveryNiche;
    
    if (!nicheToSearch) {
      toast.warning('Please enter a niche to search');
      return;
    }
    
    setDiscoveryInProgress(true);
    setDiscoveryResults(null);
    setSelectedLeads([]);
    try {
      // Start the background discovery task with single niche for speed
      const res = await axios.post(`${API_URL}/admin/influencers/discover`, {
        niches: [nicheToSearch],  // Single niche for faster results
        min_followers: 10000,
        max_results: 20,
        email_only: emailOnly
      }, getAuthHeaders());
      
      if (res.data.task_id) {
        // Poll for task completion
        toast.info(`Searching "${nicheToSearch}"... Usually takes 30-60 seconds.`);
        pollDiscoveryStatus(res.data.task_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Discovery failed');
      setDiscoveryInProgress(false);
    }
  };
  
  // Accept a single lead (import immediately)
  const handleAcceptLead = async (lead) => {
    try {
      const res = await axios.post(`${API_URL}/admin/influencers/import-leads`, {
        leads: [lead]
      }, getAuthHeaders());
      
      toast.success(`@${lead.username} added to pipeline!`);
      
      // Remove from results
      setDiscoveryResults(prev => ({
        ...prev,
        leads: prev.leads.filter(l => l.username !== lead.username)
      }));
      
      // Refresh data
      fetchInfluencers();
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error('Failed to add lead');
    }
  };
  
  // Decline a lead
  const handleDeclineLead = async (lead) => {
    try {
      await axios.post(`${API_URL}/admin/influencers/decline-lead`, {
        username: lead.username
      }, getAuthHeaders());
      
      toast.info(`@${lead.username} declined`);
      
      // Remove from results
      setDiscoveryResults(prev => ({
        ...prev,
        leads: prev.leads.filter(l => l.username !== lead.username)
      }));
    } catch (err) {
      toast.error('Failed to decline lead');
    }
  };
  
  // Fetch declined leads
  const fetchDeclinedLeads = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/influencers/declined-leads`, getAuthHeaders());
      setDeclinedLeads(res.data.leads || []);
    } catch (err) {
      console.error('Error fetching declined leads:', err);
    }
  };
  
  // Restore a declined lead
  const handleRestoreLead = async (leadId, username) => {
    try {
      await axios.post(`${API_URL}/admin/influencers/restore-lead/${leadId}`, {}, getAuthHeaders());
      toast.success(`@${username} restored`);
      fetchDeclinedLeads();
    } catch (err) {
      toast.error('Failed to restore lead');
    }
  };
  
  // Import selected leads
  const handleImportSelectedLeads = async () => {
    if (selectedLeads.length === 0) {
      toast.warning('Please select at least one lead to import');
      return;
    }
    
    setImportingLeads(true);
    try {
      const res = await axios.post(`${API_URL}/admin/influencers/import-leads`, {
        leads: selectedLeads
      }, getAuthHeaders());
      
      toast.success(`Imported ${res.data.imported_count} leads! (${res.data.skipped_count} duplicates skipped)`);
      
      // Refresh data
      fetchInfluencers();
      fetchKanban();
      fetchStats();
      
      // Clear results and close modal
      setDiscoveryResults(null);
      setSelectedLeads([]);
      setShowDiscoveryModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportingLeads(false);
    }
  };
  
  // Toggle lead selection
  const toggleLeadSelection = (lead) => {
    setSelectedLeads(prev => {
      const exists = prev.find(l => l.username === lead.username);
      if (exists) {
        return prev.filter(l => l.username !== lead.username);
      } else {
        return [...prev, lead];
      }
    });
  };
  
  // Select/deselect all discovery leads
  const toggleSelectAllLeads = () => {
    if (discoveryResults?.leads) {
      if (selectedLeads.length === discoveryResults.leads.length) {
        setSelectedLeads([]);
      } else {
        setSelectedLeads([...discoveryResults.leads]);
      }
    }
  };
  
  const pollDiscoveryStatus = async (taskId) => {
    const maxAttempts = 60; // 5 minutes max (5 second intervals)
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/influencers/discover/status/${taskId}`, getAuthHeaders());
        
        if (res.data.status === 'completed') {
          setDiscoveryResults(res.data.result);
          setDiscoveryInProgress(false);
          
          const leadCount = res.data.result?.leads?.length || res.data.result?.count || 0;
          if (leadCount > 0) {
            toast.success(`Found ${leadCount} leads! Review and select which to import.`);
          } else {
            toast.info('Discovery complete - no leads found matching criteria');
          }
        } else if (res.data.status === 'failed') {
          toast.error('Discovery failed: ' + (res.data.error || 'Unknown error'));
          setDiscoveryInProgress(false);
        } else {
          // Still pending
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000); // Check every 5 seconds
          } else {
            toast.warning('Discovery is taking longer than expected. Check back later.');
            setDiscoveryInProgress(false);
          }
        }
      } catch (err) {
        console.error('Error checking discovery status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        } else {
          setDiscoveryInProgress(false);
        }
      }
    };
    
    checkStatus();
  };

  const handleSendEmail = async () => {
    if (!selectedInfluencer?.email) {
      toast.error('Influencer has no email address');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/influencers/${selectedInfluencer.id}/outreach/email`, emailData, getAuthHeaders());
      toast.success('Email sent successfully');
      setShowEmailComposer(false);
      setEmailData({ template_id: null, subject: '', html_content: '', text_content: '', custom_offer: '' });
      fetchInfluencerDetail(selectedInfluencer.id);
    } catch (err) {
      toast.error('Failed to send email: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleLogDM = async (content) => {
    try {
      await axios.post(`${API_URL}/admin/influencers/${selectedInfluencer.id}/outreach/dm`, 
        { content }, getAuthHeaders());
      toast.success('DM logged');
      fetchInfluencerDetail(selectedInfluencer.id);
    } catch (err) {
      toast.error('Failed to log DM');
    }
  };

  const handleCreateDeal = async () => {
    try {
      const data = {
        ...dealData,
        flat_fee_amount: dealData.flat_fee_amount ? parseFloat(dealData.flat_fee_amount) : null,
        commission_percent: dealData.commission_percent ? parseFloat(dealData.commission_percent) : null,
        revshare_percent: dealData.revshare_percent ? parseFloat(dealData.revshare_percent) : null,
        revshare_months: dealData.revshare_months ? parseInt(dealData.revshare_months) : null
      };
      await axios.post(`${API_URL}/admin/influencers/${selectedInfluencer.id}/deal`, data, getAuthHeaders());
      toast.success('Deal created successfully');
      setShowDealModal(false);
      fetchInfluencerDetail(selectedInfluencer.id);
      fetchKanban();
      fetchStats();
    } catch (err) {
      toast.error('Failed to create deal: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (selectedTemplate?.id) {
        await axios.put(`${API_URL}/admin/influencer-templates/${selectedTemplate.id}`, templateForm, getAuthHeaders());
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/admin/influencer-templates`, templateForm, getAuthHeaders());
        toast.success('Template created');
      }
      setShowTemplateModal(false);
      setSelectedTemplate(null);
      setTemplateForm({ name: '', category: 'cold_outreach', subject: '', html_content: '', text_content: '', description: '' });
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to save template');
    }
  };

  const handleUseTemplate = (template) => {
    setEmailData({
      template_id: template.id,
      subject: template.subject,
      html_content: template.html_content || '',
      text_content: template.text_content || '',
      custom_offer: ''
    });
    setShowEmailComposer(true);
  };

  const openInfluencerDetail = (inf) => {
    fetchInfluencerDetail(inf.id);
    setShowDetailDrawer(true);
  };

  // ================== RENDER HELPERS ==================

  const StageLabel = ({ stage }) => {
    const stageConfig = STAGES.find(s => s.id === stage) || STAGES[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${stageConfig.color}`}>
        {stageConfig.label}
      </span>
    );
  };

  // ================== SUB-COMPONENTS ==================

  // Automation Status Card (Smartlead pipeline)
  const AutomationCard = () => {
    if (!automationStats) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader size={14} className="animate-spin" /> Loading automation status...
          </div>
        </div>
      );
    }

    const { settings: aSettings, today, queues, smartlead_configured, mailbox_health } = automationStats;
    const isPaused = aSettings.is_paused;
    const capPct = today.cap > 0 ? Math.min(100, Math.round((today.enrolled / today.cap) * 100)) : 0;

    return (
      <div className={`rounded-xl border shadow-sm p-4 mb-4 ${isPaused ? 'bg-amber-50 border-amber-200' : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200'}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Zap size={18} className={isPaused ? 'text-amber-600' : 'text-indigo-600'} />
              <h3 className="font-semibold text-gray-900">Outreach Automation</h3>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isPaused ? 'bg-amber-200 text-amber-900' : 'bg-emerald-100 text-emerald-700'}`}>
                {isPaused ? 'PAUSED' : 'RUNNING'}
              </span>
              {!smartlead_configured && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-1">
                  <AlertCircle size={12} /> Smartlead not configured
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Discovered leads auto-enrolled into Smartlead drip every 30 min. Replies are AI-classified and positive intents auto-create affiliate codes.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleToggleAutomationPause}
              disabled={automationLoading || !smartlead_configured}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                isPaused
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              } disabled:opacity-50`}
            >
              {isPaused ? <><Play size={14} /> Resume</> : <><Pause size={14} /> Pause</>}
            </button>
            <button
              onClick={handleRunEnrollmentNow}
              disabled={automationLoading || isPaused || !smartlead_configured}
              className="px-3 py-1.5 rounded-lg text-sm bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              title="Run auto-enrollment now"
            >
              <RefreshCw size={14} className="inline mr-1" /> Run now
            </button>
            <button
              onClick={fetchAutomationStats}
              className="p-1.5 text-gray-400 hover:text-gray-600"
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
          <div className="bg-white/70 rounded-lg p-3">
            <div className="text-xs text-gray-500">Today: enrolled</div>
            <div className="text-xl font-semibold text-gray-900 mt-0.5">
              {today.enrolled}
              <span className="text-sm text-gray-400 font-normal"> / {today.cap}</span>
            </div>
            <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full ${capPct >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${capPct}%` }} />
            </div>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-[10px] text-gray-500">{today.remaining} left</span>
              <button
                onClick={() => {
                  const v = window.prompt('New daily enrollment cap:', String(aSettings.daily_enrollment_cap));
                  const n = parseInt(v, 10);
                  if (!isNaN(n) && n >= 0) handleUpdateAutomationCap(n);
                }}
                className="text-[10px] text-indigo-600 hover:underline"
              >
                edit cap
              </button>
            </div>
          </div>

          <div className="bg-white/70 rounded-lg p-3">
            <div className="text-xs text-gray-500">Replies today</div>
            <div className="text-xl font-semibold text-gray-900 mt-0.5">{today.replies}</div>
            <div className="text-[11px] text-emerald-700 mt-1">{today.positive_replies} positive</div>
          </div>

          <div className="bg-white/70 rounded-lg p-3">
            <div className="text-xs text-gray-500">In sequence</div>
            <div className="text-xl font-semibold text-gray-900 mt-0.5">{queues.in_sequence}</div>
            <div className="text-[11px] text-gray-500 mt-1">{queues.pending_leads} pending leads</div>
          </div>

          <div
            className={`bg-white/70 rounded-lg p-3 ${queues.needs_review > 0 ? 'ring-2 ring-amber-300 cursor-pointer' : ''}`}
            onClick={() => { if (queues.needs_review > 0) { setShowNeedsReview(true); fetchNeedsReview(); } }}
          >
            <div className="text-xs text-gray-500">Needs review</div>
            <div className={`text-xl font-semibold mt-0.5 ${queues.needs_review > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
              {queues.needs_review}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">{queues.suppressed} suppressed</div>
          </div>

          <div className="bg-white/70 rounded-lg p-3">
            <div className="text-xs text-gray-500">Mailboxes</div>
            <div className="text-xl font-semibold text-gray-900 mt-0.5">{mailbox_health?.length || 0}</div>
            <div className="text-[11px] text-gray-500 mt-1">
              {(mailbox_health || []).filter(m => m.bounce_rate_pct > aSettings.bounce_threshold_pct).length} unhealthy
            </div>
          </div>
        </div>

        {(mailbox_health || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {mailbox_health.map(mb => {
              const unhealthy = mb.bounce_rate_pct > aSettings.bounce_threshold_pct;
              return (
                <span
                  key={mb.id || mb.email}
                  title={`Sent ${mb.sent}, bounced ${mb.bounced} (${mb.bounce_rate_pct}%), warmup: ${mb.warmup_status || 'n/a'}`}
                  className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${
                    unhealthy ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {mb.email} {mb.bounce_rate_pct}%
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Needs-Review modal (AI-flagged ambiguous replies)
  const NeedsReviewModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Needs Review</h3>
            <p className="text-xs text-gray-500">AI classifier was uncertain or the reply contained a question.</p>
          </div>
          <button onClick={() => setShowNeedsReview(false)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          {needsReviewList.length === 0 && (
            <div className="text-center text-sm text-gray-500 py-12">Nothing to review.</div>
          )}
          {needsReviewList.map(item => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Instagram size={14} className="text-gray-400 flex-shrink-0" />
                  <span className="font-medium text-sm truncate">{item.instagram_handle}</span>
                  <span className="text-xs text-gray-500 truncate">{item.email}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                    {item.last_reply_intent || 'unknown'}
                    {item.last_reply_classifier_confidence != null && ` · ${Math.round(item.last_reply_classifier_confidence * 100)}%`}
                  </span>
                  <button
                    onClick={() => handleClearNeedsReview(item.id)}
                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Clear flag
                  </button>
                </div>
              </div>
              {item.last_reply_body && (
                <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap font-sans bg-gray-50 rounded p-2 max-h-40 overflow-y-auto">
                  {item.last_reply_body}
                </pre>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Stats Bar
  const StatsBar = () => (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
      {STAGES.map(stage => (
        <div key={stage.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{stage.label}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${stage.color}`}>
              {stats?.by_stage?.[stage.id] || 0}
            </span>
          </div>
        </div>
      ))}
      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Response Rate</span>
          <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700 font-medium">
            {stats?.response_rate || 0}%
          </span>
        </div>
      </div>
      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Needs Attention</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stats?.needs_attention > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
            {stats?.needs_attention || 0}
          </span>
        </div>
      </div>
    </div>
  );

  // Kanban Board
  const KanbanBoard = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {STAGES.map(stage => (
        <div key={stage.id} className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-700">{stage.label}</h3>
            <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">
              {kanbanData[stage.id]?.length || 0}
            </span>
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {(kanbanData[stage.id] || []).map(inf => (
              <div 
                key={inf.id}
                onClick={() => openInfluencerDetail(inf)}
                className={`bg-white rounded-lg p-3 shadow-sm border cursor-pointer hover:shadow-md transition-shadow ${inf.days_since_contact > 7 && stage.id === 'contacted' ? 'border-red-300' : 'border-gray-200'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{inf.instagram_handle}</p>
                    {inf.full_name && <p className="text-xs text-gray-500 truncate">{inf.full_name}</p>}
                  </div>
                  {inf.follower_count && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded ml-2">
                      {formatFollowers(inf.follower_count)}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                  {inf.niche && <span className="text-xs text-gray-400 truncate">{inf.niche}</span>}
                  <span className="flex-1" />
                  {inf.drip_status && inf.drip_status !== 'not_started' && (
                    <DripBadge status={inf.drip_status} />
                  )}
                  {inf.email ? <Mail size={12} className="text-green-500" /> : <span className="text-xs text-gray-300">No email</span>}
                </div>
                {inf.days_since_contact != null && (
                  <p className={`text-[10px] mt-1 ${inf.days_since_contact > 7 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                    {inf.days_since_contact === 0 ? 'Contacted today' : `${inf.days_since_contact}d since contact`}
                  </p>
                )}
              </div>
            ))}
            {(!kanbanData[stage.id] || kanbanData[stage.id].length === 0) && (
              <div className="text-center py-8 text-gray-400 text-sm">No influencers</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // Table View
  const SortHeader = ({ label, field }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
      onClick={() => { setSortBy(field); setSortDir(sortBy === field && sortDir === 'asc' ? 'desc' : 'asc'); }}
    >
      {label} {sortBy === field && (sortDir === 'asc' ? <ChevronUp size={12} className="inline" /> : <ChevronDown size={12} className="inline" />)}
    </th>
  );

  const DripBadge = ({ status }) => {
    const cfg = DRIP_STATUSES[status] || DRIP_STATUSES.not_started;
    return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cfg.color}`}>{cfg.label}</span>;
  };

  const TableView = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 w-8">
                <input type="checkbox" checked={selectedIds.size === influencers.length && influencers.length > 0} onChange={toggleSelectAll} className="rounded border-gray-300" />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Handle</th>
              <SortHeader label="Name" field="name" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Niche</th>
              <SortHeader label="Followers" field="followers" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drip</th>
              <SortHeader label="Last Contact" field="last_contacted" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {influencers.map(inf => (
              <tr key={inf.id} className={`hover:bg-gray-50 ${selectedIds.has(inf.id) ? 'bg-indigo-50' : ''}`}>
                <td className="px-3 py-3">
                  <input type="checkbox" checked={selectedIds.has(inf.id)} onChange={() => toggleSelectId(inf.id)} className="rounded border-gray-300" />
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="font-medium text-indigo-600">{inf.instagram_handle}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{inf.full_name || '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{inf.email || '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{inf.niche || '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatFollowers(inf.follower_count)}</td>
                <td className="px-4 py-3 whitespace-nowrap"><StageLabel stage={inf.stage} /></td>
                <td className="px-4 py-3 whitespace-nowrap"><DripBadge status={inf.drip_status || 'not_started'} /></td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {inf.last_contacted_at ? (
                    <span className={inf.days_since_contact > 7 ? 'text-red-500 font-medium' : ''}>
                      {new Date(inf.last_contacted_at).toLocaleDateString()}
                      {inf.days_since_contact != null && <span className="text-xs ml-1">({inf.days_since_contact}d ago)</span>}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={() => openInfluencerDetail(inf)} className="text-indigo-600 hover:text-indigo-900">
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Templates List
  const TemplatesList = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Email Templates</h3>
        <button
          onClick={() => {
            setSelectedTemplate(null);
            setTemplateForm({ name: '', category: 'cold_outreach', subject: '', html_content: '', text_content: '', description: '' });
            setShowTemplateModal(true);
          }}
          className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <Plus size={16} className="mr-1" /> New Template
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map(template => (
          <div key={template.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-medium text-gray-900">{template.name}</h4>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded mt-1 inline-block">
                  {TEMPLATE_CATEGORIES.find(c => c.id === template.category)?.label || template.category}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setSelectedTemplate(template);
                    axios.get(`${API_URL}/admin/influencer-templates/${template.id}`, getAuthHeaders())
                      .then(res => {
                        setTemplateForm({
                          name: res.data.name,
                          category: res.data.category,
                          subject: res.data.subject,
                          html_content: res.data.html_content || '',
                          text_content: res.data.text_content || '',
                          description: res.data.description || ''
                        });
                        setShowTemplateModal(true);
                      });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Edit2 size={14} />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-2 truncate">Subject: {template.subject}</p>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
              <span>Used {template.times_used || 0} times</span>
              {selectedInfluencer && (
                <button
                  onClick={() => handleUseTemplate(template)}
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Use Template
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Import Modal
  const ImportModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Import Influencers from CSV</h3>
          <button onClick={closeImportModal} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {importResult ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-medium">
                  Successfully imported {importResult.imported} influencer(s).
                  {importResult.skipped > 0 && ` ${importResult.skipped} skipped.`}
                </p>
              </div>
              {importResult.duplicates?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 font-medium mb-1">{importResult.duplicates.length} duplicate(s) skipped:</p>
                  <ul className="text-xs text-yellow-700 space-y-0.5">
                    {importResult.duplicates.slice(0, 5).map((d, i) => (
                      <li key={i}>Row {d.row}: {d.handle}</li>
                    ))}
                    {importResult.duplicates.length > 5 && <li>...and {importResult.duplicates.length - 5} more</li>}
                  </ul>
                </div>
              )}
              {importResult.unmapped_columns?.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Unmapped columns ignored: {importResult.unmapped_columns.join(', ')}</p>
                </div>
              )}
              {importResult.missing_data_count > 0 && importResult.imported_ids?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 mb-2">
                    {importResult.missing_data_count} imported influencer(s) are missing email or follower data.
                  </p>
                  <button
                    onClick={() => { handleEnrich(importResult.imported_ids); closeImportModal(); }}
                    disabled={enriching}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {enriching ? <><Loader size={14} className="inline mr-1 animate-spin" /> Enriching...</> : 'Enrich Profiles via Instagram'}
                  </button>
                </div>
              )}
              <div className="flex justify-end">
                <button onClick={closeImportModal} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => setCsvData({ ...csvData, importMode: 'file', preview: null })}
                  className={`px-3 py-1.5 text-sm rounded-lg ${csvData.importMode === 'file' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'bg-gray-100 text-gray-600'}`}
                >
                  <Upload size={14} className="inline mr-1" /> Upload File
                </button>
                <button
                  onClick={() => setCsvData({ ...csvData, importMode: 'paste', preview: null })}
                  className={`px-3 py-1.5 text-sm rounded-lg ${csvData.importMode === 'paste' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'bg-gray-100 text-gray-600'}`}
                >
                  <FileText size={14} className="inline mr-1" /> Paste Text
                </button>
              </div>

              {csvData.importMode === 'file' ? (
                <div
                  className={`mb-4 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${csvDragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50'}`}
                  onDragOver={(e) => { e.preventDefault(); setCsvDragActive(true); }}
                  onDragLeave={() => setCsvDragActive(false)}
                  onDrop={handleCsvDrop}
                >
                  {csvFile ? (
                    <div className="space-y-2">
                      <FileText size={32} className="mx-auto text-indigo-500" />
                      <p className="text-sm font-medium text-gray-900">{csvFile.name}</p>
                      <p className="text-xs text-gray-500">{(csvFile.size / 1024).toFixed(1)} KB</p>
                      <button onClick={() => { setCsvFile(null); setCsvData({ ...csvData, content: '', preview: null }); }} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload size={32} className="mx-auto text-gray-400" />
                      <p className="text-sm text-gray-600">Drag & drop a CSV file here, or</p>
                      <label className="inline-block px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 cursor-pointer">
                        Browse Files
                        <input type="file" accept=".csv,.txt,.tsv" className="hidden" onChange={(e) => handleCsvFileSelect(e.target.files[0])} />
                      </label>
                      <p className="text-xs text-gray-400">Supports .csv, .txt, .tsv files</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Paste CSV Content</label>
                  <textarea
                    value={csvData.content}
                    onChange={(e) => setCsvData({ ...csvData, content: e.target.value, preview: null })}
                    placeholder="instagram_handle,email,name,niche,follower_count,notes&#10;@creator1,email@example.com,John,fitness,50K,Great content"
                    className="w-full h-40 p-3 border border-gray-300 rounded-lg font-mono text-sm"
                  />
                </div>
              )}
              
              {!csvData.preview ? (
                <button
                  onClick={handlePreviewImport}
                  disabled={!csvData.content.trim() || importLoading}
                  className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  {importLoading ? <><Loader size={14} className="inline mr-1 animate-spin" /> Parsing...</> : 'Preview Import'}
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>{csvData.preview.total_rows}</strong> rows detected. 
                      Columns mapped: {Object.keys(csvData.preview.detected_mapping).join(', ')}
                    </p>
                    {csvData.preview.unmapped_columns?.length > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        Unmapped (will be ignored): {csvData.preview.unmapped_columns.join(', ')}
                      </p>
                    )}
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(csvData.preview.detected_mapping).map(col => (
                            <th key={col} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {csvData.preview.sample_rows.map((row, i) => (
                          <tr key={i}>
                            {Object.keys(csvData.preview.detected_mapping).map(col => (
                              <td key={col} className="px-3 py-2 text-gray-700">{row[col] != null ? String(row[col]) : '—'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {!importResult && (
          <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
            <button onClick={closeImportModal} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Cancel
            </button>
            {csvData.preview && (
              <button
                onClick={handleImport}
                disabled={importLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {importLoading ? <><Loader size={14} className="inline mr-1 animate-spin" /> Importing...</> : `Import ${csvData.preview.total_rows} Rows`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Add Influencer Modal
  const AddInfluencerModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Add Influencer</h3>
          <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instagram Handle *</label>
            <input
              type="text"
              value={newInfluencer.instagram_handle}
              onChange={(e) => setNewInfluencer({ ...newInfluencer, instagram_handle: e.target.value })}
              placeholder="@username"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={newInfluencer.email}
              onChange={(e) => setNewInfluencer({ ...newInfluencer, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={newInfluencer.full_name}
              onChange={(e) => setNewInfluencer({ ...newInfluencer, full_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Niche</label>
              <input
                type="text"
                value={newInfluencer.niche}
                onChange={(e) => setNewInfluencer({ ...newInfluencer, niche: e.target.value })}
                placeholder="e.g., IG Growth"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Followers</label>
              <input
                type="number"
                value={newInfluencer.follower_count}
                onChange={(e) => setNewInfluencer({ ...newInfluencer, follower_count: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={newInfluencer.notes}
              onChange={(e) => setNewInfluencer({ ...newInfluencer, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20"
            />
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancel
          </button>
          <button
            onClick={handleAddInfluencer}
            disabled={!newInfluencer.instagram_handle.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            Add Influencer
          </button>
        </div>
      </div>
    </div>
  );

  // Detail Drawer
  const [dmContent, setDmContent] = useState('');
  
  const DetailDrawer = () => {
    if (!selectedInfluencer) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
        <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
            <h2 className="text-lg font-semibold text-gray-900">{selectedInfluencer.instagram_handle}</h2>
            <button onClick={() => { setShowDetailDrawer(false); setSelectedInfluencer(null); }} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase">Name</label>
                <p className="font-medium">{selectedInfluencer.full_name || '—'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Followers</label>
                <p className="font-medium">{formatFollowers(selectedInfluencer.follower_count)}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Email</label>
                <p className="font-medium">{selectedInfluencer.email || '—'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Niche</label>
                <p className="font-medium">{selectedInfluencer.niche || '—'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Stage</label>
                <div className="mt-1">
                  <select
                    value={selectedInfluencer.stage}
                    onChange={(e) => handleUpdateStage(selectedInfluencer.id, e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Last Contacted</label>
                <p className="font-medium">
                  {selectedInfluencer.last_contacted_at 
                    ? new Date(selectedInfluencer.last_contacted_at).toLocaleDateString() 
                    : 'Never'}
                </p>
              </div>
            </div>
            
            {/* Notes */}
            {selectedInfluencer.notes && (
              <div>
                <label className="text-xs text-gray-500 uppercase">Notes</label>
                <p className="text-gray-700 mt-1">{selectedInfluencer.notes}</p>
              </div>
            )}
            
            {/* Deal Info */}
            {selectedInfluencer.deal ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-green-800">Active Deal</h4>
                    <p className="text-sm text-green-600">{selectedInfluencer.deal.summary}</p>
                  </div>
                  {selectedInfluencer.deal.affiliate_link && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Affiliate Link</p>
                      <button
                        onClick={() => { navigator.clipboard.writeText(selectedInfluencer.deal.affiliate_link); toast.success('Copied!'); }}
                        className="text-sm text-indigo-600 flex items-center"
                      >
                        <Copy size={12} className="mr-1" /> Copy
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowDealModal(true)}
                className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-indigo-500 hover:text-indigo-600"
              >
                <DollarSign size={16} className="inline mr-1" /> Create Deal
              </button>
            )}
            
            {/* Drip Sequence Status */}
            {selectedInfluencer.email && dripStatus && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Email Drip Sequence</h4>
                  {(() => {
                    const status = DRIP_STATUSES[dripStatus.status] || DRIP_STATUSES.not_started;
                    const Icon = status.icon;
                    return (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${status.color}`}>
                        <Icon size={12} className="mr-1" /> {status.label}
                      </span>
                    );
                  })()}
                </div>
                
                {dripStatus.status === 'in_sequence' && (
                  <div className="mb-3">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{dripStatus.current_email} / {dripStatus.total_emails} emails</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all" 
                        style={{ width: `${(dripStatus.current_email / dripStatus.total_emails) * 100}%` }}
                      />
                    </div>
                    {dripStatus.next_scheduled_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        Next email: {new Date(dripStatus.next_scheduled_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
                
                <div className="flex space-x-2">
                  {dripStatus.status === 'not_started' && (
                    <button
                      onClick={handleStartDrip}
                      className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center text-sm"
                    >
                      <Play size={14} className="mr-1" /> Start Sequence
                    </button>
                  )}
                  {dripStatus.status === 'in_sequence' && (
                    <>
                      <button
                        onClick={handleStopDrip}
                        className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center text-sm"
                      >
                        <Pause size={14} className="mr-1" /> Stop
                      </button>
                      <button
                        onClick={handleMarkReplied}
                        className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center text-sm"
                      >
                        <CheckCircle size={14} className="mr-1" /> Replied
                      </button>
                    </>
                  )}
                  {(dripStatus.status === 'stopped' || dripStatus.status === 'completed') && (
                    <button
                      onClick={handleStartDrip}
                      className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center text-sm"
                    >
                      <RefreshCw size={14} className="mr-1" /> Restart Sequence
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex space-x-3">
              {selectedInfluencer.email ? (
                <button
                  onClick={() => setShowEmailComposer(true)}
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center"
                >
                  <Mail size={16} className="mr-2" /> Send Email
                </button>
              ) : (
                <button
                  onClick={() => handlePrepareDM()}
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center"
                >
                  <MessageSquare size={16} className="mr-2" /> Prepare DM
                </button>
              )}
              <a
                href={`https://instagram.com/${selectedInfluencer.instagram_handle.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg flex items-center justify-center"
              >
                <Instagram size={16} className="mr-2" /> View Profile
              </a>
            </div>
            
            {/* Log DM */}
            <div>
              <label className="text-xs text-gray-500 uppercase mb-2 block">Log Instagram DM</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={dmContent}
                  onChange={(e) => setDmContent(e.target.value)}
                  placeholder="Summary of DM sent..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                />
                <button
                  onClick={() => { handleLogDM(dmContent); setDmContent(''); }}
                  disabled={!dmContent.trim()}
                  className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Log
                </button>
              </div>
            </div>
            
            {/* Communication History */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Communication History ({selectedInfluencer.outreach_count})</h4>
              {selectedInfluencer.outreach_history?.length > 0 ? (
                <div className="space-y-3">
                  {selectedInfluencer.outreach_history.map(o => (
                    <div key={o.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {o.channel === 'email' ? <Mail size={14} className="text-blue-500" /> : <MessageSquare size={14} className="text-purple-500" />}
                          <span className="text-sm font-medium">{o.channel === 'email' ? 'Email' : 'Instagram DM'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-400">{new Date(o.sent_at).toLocaleString()}</span>
                          {o.channel === 'email' && (
                            <button
                              onClick={() => handlePreviewEmail(o.id)}
                              className="text-blue-500 hover:text-blue-700 p-1"
                              title="Preview email"
                            >
                              <Eye size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      {o.subject && <p className="text-sm text-gray-700 mt-1">{o.subject}</p>}
                      {o.channel === 'email' && (
                        <div className="flex space-x-4 mt-2 text-xs">
                          <span className={o.email_opened ? 'text-green-600' : 'text-gray-400'}>
                            {o.email_opened ? '✓ Opened' : 'Not opened'}
                          </span>
                          <span className={o.email_clicked ? 'text-green-600' : 'text-gray-400'}>
                            {o.email_clicked ? '✓ Clicked' : 'No clicks'}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No outreach yet</p>
              )}
            </div>
            
            {/* Delete Button */}
            <button
              onClick={() => handleDeleteInfluencer(selectedInfluencer.id)}
              className="w-full py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
            >
              <Trash2 size={16} className="inline mr-1" /> Delete Influencer
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Email Composer Modal
  const EmailComposerModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Send Email</h3>
            <p className="text-sm text-gray-500">To: {selectedInfluencer?.email}</p>
          </div>
          <button onClick={() => setShowEmailComposer(false)} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template (optional)</label>
            <select
              value={emailData.template_id || ''}
              onChange={(e) => {
                const id = e.target.value;
                if (id) {
                  const t = templates.find(t => t.id === parseInt(id));
                  if (t) {
                    axios.get(`${API_URL}/admin/influencer-templates/${t.id}`, getAuthHeaders())
                      .then(res => {
                        setEmailData({
                          ...emailData,
                          template_id: t.id,
                          subject: res.data.subject,
                          html_content: res.data.html_content || '',
                          text_content: res.data.text_content || ''
                        });
                      });
                  }
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select a template...</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
            <input
              type="text"
              value={emailData.subject}
              onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Offer (replaces {"{{custom_offer}}"} in template)</label>
            <textarea
              value={emailData.custom_offer}
              onChange={(e) => setEmailData({ ...emailData, custom_offer: e.target.value })}
              placeholder="e.g., 20% commission on all sales + $50 signup bonus"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Content *</label>
            <ReactQuill
              value={emailData.html_content}
              onChange={(value) => setEmailData({ ...emailData, html_content: value })}
              className="bg-white"
              style={{ minHeight: '200px' }}
            />
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
            <strong>Variables:</strong> {"{{influencer_name}}"}, {"{{handle}}"}, {"{{follower_count}}"}, {"{{niche}}"}, {"{{custom_offer}}"}, {"{{sender_name}}"}
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={() => setShowEmailComposer(false)}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSendEmail}
            disabled={!emailData.subject.trim() || !emailData.html_content.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center"
          >
            <Send size={16} className="mr-2" /> Send Email
          </button>
        </div>
      </div>
    </div>
  );

  // Deal Modal
  const DealModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Create Deal</h3>
          <button onClick={() => setShowDealModal(false)} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Type</label>
            <select
              value={dealData.deal_type}
              onChange={(e) => setDealData({ ...dealData, deal_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {DEAL_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          
          {dealData.deal_type === 'flat_fee' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount per Conversion ($)</label>
              <input
                type="number"
                value={dealData.flat_fee_amount}
                onChange={(e) => setDealData({ ...dealData, flat_fee_amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}
          
          {dealData.deal_type === 'commission_percent' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commission (%)</label>
              <input
                type="number"
                value={dealData.commission_percent}
                onChange={(e) => setDealData({ ...dealData, commission_percent: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}
          
          {dealData.deal_type === 'revenue_share' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Share (%)</label>
                <input
                  type="number"
                  value={dealData.revshare_percent}
                  onChange={(e) => setDealData({ ...dealData, revshare_percent: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (months)</label>
                <input
                  type="number"
                  value={dealData.revshare_months}
                  onChange={(e) => setDealData({ ...dealData, revshare_months: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </>
          )}
          
          {dealData.deal_type === 'hybrid' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flat Fee ($)</label>
                <input
                  type="number"
                  value={dealData.flat_fee_amount}
                  onChange={(e) => setDealData({ ...dealData, flat_fee_amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commission (%)</label>
                <input
                  type="number"
                  value={dealData.commission_percent}
                  onChange={(e) => setDealData({ ...dealData, commission_percent: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Terms</label>
            <textarea
              value={dealData.custom_terms}
              onChange={(e) => setDealData({ ...dealData, custom_terms: e.target.value })}
              placeholder="Any additional terms or notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20"
            />
          </div>
          
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={dealData.activate_now}
              onChange={(e) => setDealData({ ...dealData, activate_now: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Activate deal and generate affiliate link</span>
          </label>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button onClick={() => setShowDealModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancel
          </button>
          <button onClick={handleCreateDeal} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Create Deal
          </button>
        </div>
      </div>
    </div>
  );

  // Template Modal
  const TemplateModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {selectedTemplate ? 'Edit Template' : 'Create Template'}
          </h3>
          <button onClick={() => { setShowTemplateModal(false); setSelectedTemplate(null); }} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
              <input
                type="text"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                value={templateForm.category}
                onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {TEMPLATE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
            <input
              type="text"
              value={templateForm.subject}
              onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (internal)</label>
            <input
              type="text"
              value={templateForm.description}
              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Content *</label>
            <ReactQuill
              value={templateForm.html_content}
              onChange={(value) => setTemplateForm({ ...templateForm, html_content: value })}
              className="bg-white"
              style={{ minHeight: '200px' }}
            />
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <strong>Available Variables:</strong> {"{{influencer_name}}"}, {"{{handle}}"}, {"{{follower_count}}"}, {"{{niche}}"}, {"{{custom_offer}}"}, {"{{sender_name}}"}
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={() => { setShowTemplateModal(false); setSelectedTemplate(null); }}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveTemplate}
            disabled={!templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.html_content.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {selectedTemplate ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );

  // ================== MAIN RENDER ==================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Influencer Outreach</h2>
          <p className="text-gray-500">Manage influencer partnerships and outreach</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { setShowProspectsModal(true); fetchProspects(); fetchProspectsStats(); }}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600"
          >
            <Users size={16} className="mr-2" /> Prospects
            {prospectsStats?.pending > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-white text-amber-600 text-xs font-bold rounded-full">
                {prospectsStats.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => { setShowNicheConfig(true); fetchNiches(); }}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Settings size={16} className="mr-2" /> Discovery Settings
          </button>
          <button
            onClick={() => { setShowDeclinedModal(true); fetchDeclinedLeads(); }}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Eye size={16} className="mr-2" /> Declined
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Upload size={16} className="mr-2" /> Import CSV
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus size={16} className="mr-2" /> Add Influencer
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setSubTab('pipeline')}
          className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
            subTab === 'pipeline' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Pipeline
        </button>
        <button
          onClick={() => setSubTab('templates')}
          className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
            subTab === 'templates' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Email Templates
        </button>
      </div>

      {subTab === 'pipeline' && (
        <>
          {/* Automation Status (Smartlead pipeline) */}
          <AutomationCard />

          {/* Manual / advanced controls toggle */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              {automationStats?.settings?.is_paused
                ? 'Automation is paused — use manual controls below to send one-offs.'
                : 'Automation is running. Manual sends bypass the warmup-protected mailbox rotation.'}
            </span>
            <button
              onClick={() => setShowAdvancedManual(v => !v)}
              className="text-indigo-600 hover:underline"
            >
              {showAdvancedManual ? 'Hide manual controls' : 'Show manual controls'}
            </button>
          </div>

          {/* Stats */}
          <StatsBar />
          
          {/* View Toggle & Filters */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 rounded text-sm ${viewMode === 'kanban' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}
              >
                Kanban
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded text-sm ${viewMode === 'table' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}
              >
                Table
              </button>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm w-48"
                />
              </div>
              <select
                value={filters.stage}
                onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Stages</option>
                {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <select
                value={filters.has_email}
                onChange={(e) => setFilters({ ...filters, has_email: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All</option>
                <option value="yes">Has Email</option>
                <option value="no">No Email</option>
              </select>
              <button
                onClick={() => { fetchInfluencers(); fetchKanban(); }}
                className="p-1.5 text-gray-400 hover:text-gray-600"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          </div>

          {/* Bulk Actions Toolbar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <span className="text-sm font-medium text-indigo-700">{selectedIds.size} selected</span>
              <span className="text-gray-300">|</span>
              {showAdvancedManual && (
                <>
                  <button onClick={() => { setShowBulkEmailModal(true); }} className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700" disabled={bulkLoading}>
                    <Mail size={14} className="inline mr-1" /> Email All
                  </button>
                  <button onClick={handleBulkDripStart} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700" disabled={bulkLoading}>
                    <Play size={14} className="inline mr-1" /> Start Drip
                  </button>
                </>
              )}
              <select
                onChange={(e) => { if (e.target.value) handleBulkStageUpdate(e.target.value); }}
                className="px-2 py-1 text-sm border border-gray-300 rounded"
                defaultValue=""
              >
                <option value="" disabled>Move to...</option>
                {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button onClick={() => handleEnrich(Array.from(selectedIds))} className="px-3 py-1 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700" disabled={enriching}>
                <Zap size={14} className="inline mr-1" /> Enrich
              </button>
              <button onClick={handleBulkDelete} className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600" disabled={bulkLoading}>
                <Trash2 size={14} className="inline mr-1" /> Delete
              </button>
              <span className="flex-1" />
              <button onClick={() => setSelectedIds(new Set())} className="text-xs text-gray-500 hover:text-gray-700">Clear selection</button>
            </div>
          )}

          {/* Quick Outreach Button (manual / advanced only) */}
          {showAdvancedManual && stats?.by_stage?.lead > 0 && stats?.with_email > 0 && selectedIds.size === 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowQuickOutreachModal(true)}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 text-sm"
              >
                <Zap size={16} className="mr-2" /> Quick Outreach to All Leads
              </button>
              {enriching && (
                <span className="flex items-center text-sm text-blue-600">
                  <Loader size={14} className="mr-1 animate-spin" /> Enriching profiles...
                </span>
              )}
            </div>
          )}

          {/* View */}
          {viewMode === 'kanban' ? <KanbanBoard /> : <TableView />}
        </>
      )}

      {subTab === 'templates' && <TemplatesList />}

      {/* Modals */}
      {showImportModal && <ImportModal />}
      {showAddModal && <AddInfluencerModal />}
      {showNeedsReview && <NeedsReviewModal />}

      {/* Bulk Email Modal */}
      {showBulkEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Send Bulk Email</h3>
              <button onClick={() => setShowBulkEmailModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Send email to <strong>{selectedIds.size}</strong> selected influencer(s). Only those with email addresses will receive it.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Template</label>
                <select
                  value={bulkEmailTemplateId || ''}
                  onChange={(e) => setBulkEmailTemplateId(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select a template...</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button onClick={() => setShowBulkEmailModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleBulkEmail} disabled={!bulkEmailTemplateId || bulkLoading} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {bulkLoading ? <><Loader size={14} className="inline mr-1 animate-spin" /> Sending...</> : `Send to ${selectedIds.size} Influencers`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Outreach Modal */}
      {showQuickOutreachModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Quick Outreach</h3>
              <button onClick={() => setShowQuickOutreachModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  This will send a cold outreach email to <strong>all leads with email</strong> who haven't been contacted yet.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Template</label>
                <select
                  value={quickOutreachTemplateId || ''}
                  onChange={(e) => setQuickOutreachTemplateId(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select a template...</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button onClick={() => setShowQuickOutreachModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleQuickOutreach} disabled={!quickOutreachTemplateId || bulkLoading} className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50">
                {bulkLoading ? <><Loader size={14} className="inline mr-1 animate-spin" /> Sending...</> : 'Send Outreach'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDetailDrawer && <DetailDrawer />}
      {showEmailComposer && <EmailComposerModal />}
      {showDealModal && <DealModal />}
      {showTemplateModal && <TemplateModal />}
      
      {/* DM Composer Modal */}
      {showDMComposer && preparedDM && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Send Instagram DM</h3>
                <button onClick={() => { setShowDMComposer(false); setPreparedDM(null); }} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Template</label>
                  <select
                    value={preparedDM.template_id || ''}
                    onChange={(e) => handlePrepareDM(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {dmTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Message ({preparedDM.character_count} chars)</label>
                  <textarea
                    value={preparedDM.content}
                    readOnly
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm"
                  />
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => { navigator.clipboard.writeText(preparedDM.content); toast.success('Copied to clipboard!'); }}
                    className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center"
                  >
                    <Copy size={16} className="mr-2" /> Copy Message
                  </button>
                  <a
                    href={preparedDM.dm_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg flex items-center justify-center"
                  >
                    <Instagram size={16} className="mr-2" /> Open Instagram
                  </a>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <button
                    onClick={handleLogDMSent}
                    className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center"
                  >
                    <Check size={16} className="mr-2" /> I Sent the DM - Log It
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Click after sending to update outreach history
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Discovery Modal */}
      {showDiscoveryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Zap className="mr-2 text-amber-500" size={20} />
                  Discover Influencer Leads
                </h3>
                <button onClick={() => { setShowDiscoveryModal(false); setDiscoveryResults(null); setSelectedLeads([]); }} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Discovery Options - shown when not in progress and no results */}
                {!discoveryInProgress && !discoveryResults && (
                  <>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-medium text-amber-800 mb-2">How it works</h4>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• Searches Instagram for influencers in IG growth, marketing, and tips niches</li>
                        <li>• Filters for accounts with 10K+ followers</li>
                        <li>• You can review and select which leads to import</li>
                        <li>• Uses Oxylabs Web Scraper API via Google search</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                      <h4 className="font-medium text-gray-900">Discovery Options</h4>
                      
                      {/* Niche selector */}
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Select Niche</label>
                        <select
                          value={discoveryNiche}
                          onChange={(e) => setDiscoveryNiche(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        >
                          {DISCOVERY_NICHES.map(niche => (
                            <option key={niche} value={niche}>
                              {niche === 'custom' ? '✏️ Enter custom niche...' : niche}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Custom niche input */}
                      {discoveryNiche === 'custom' && (
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Custom Niche</label>
                          <input
                            type="text"
                            value={customNiche}
                            onChange={(e) => setCustomNiche(e.target.value)}
                            placeholder="e.g., fitness coach, travel blogger..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          />
                        </div>
                      )}
                      
                      {/* Email only checkbox */}
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={discoveryEmailOnly}
                          onChange={(e) => setDiscoveryEmailOnly(e.target.checked)}
                          className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                        />
                        <Mail size={16} className="ml-2 mr-2 text-gray-500" />
                        <span className="text-gray-700">Only find influencers with email in bio</span>
                      </label>
                    </div>
                    
                    <button
                      onClick={() => handleDiscoverLeads(discoveryEmailOnly)}
                      disabled={discoveryNiche === 'custom' && !customNiche.trim()}
                      className={`w-full py-3 rounded-lg flex items-center justify-center font-medium ${
                        discoveryNiche === 'custom' && !customNiche.trim()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
                      }`}
                    >
                      <Globe size={18} className="mr-2" /> 
                      Start Discovery ({discoveryNiche === 'custom' ? customNiche || '...' : discoveryNiche})
                    </button>
                  </>
                )}
                
                {/* Loading state */}
                {discoveryInProgress && (
                  <div className="text-center py-8">
                    <Loader className="animate-spin text-amber-500 mx-auto mb-3" size={32} />
                    <p className="text-gray-600">Searching Instagram for influencers...</p>
                    <p className="text-sm text-gray-400">This may take 3-5 minutes</p>
                  </div>
                )}
                
                {/* Results with selection */}
                {discoveryResults && (
                  <div className="space-y-4">
                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-blue-700">{discoveryResults.stats?.profiles_checked || 0}</p>
                        <p className="text-xs text-blue-600">Profiles Checked</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-green-700">{discoveryResults.leads?.length || 0}</p>
                        <p className="text-xs text-green-600">Leads Found</p>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-purple-700">{discoveryResults.stats?.with_email || 0}</p>
                        <p className="text-xs text-purple-600">With Email</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-amber-700">{discoveryResults.leads?.length || 0}</p>
                        <p className="text-xs text-amber-600">Remaining</p>
                      </div>
                    </div>
                    
                    {/* Tinder-style lead cards */}
                    {discoveryResults.leads?.length > 0 ? (
                      <div className="space-y-4">
                        {/* Current lead card */}
                        {(() => {
                          const lead = discoveryResults.leads[0];
                          if (!lead) return null;
                          
                          return (
                            <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
                              {/* Progress indicator */}
                              <div className="text-center text-xs text-gray-400 mb-4">
                                Reviewing 1 of {discoveryResults.leads.length} leads
                              </div>
                              
                              {/* Profile info */}
                              <div className="text-center mb-6">
                                <div className="w-20 h-20 mx-auto mb-3 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center">
                                  <Instagram size={32} className="text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">@{lead.username}</h3>
                                {lead.full_name && (
                                  <p className="text-gray-600 mt-1">{lead.full_name}</p>
                                )}
                                {lead.email && (
                                  <div className="mt-2 inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                    <Mail size={14} className="mr-1" /> {lead.email}
                                  </div>
                                )}
                              </div>
                              
                              {/* Bio preview */}
                              {lead.bio && (
                                <div className="bg-gray-100 rounded-lg p-3 mb-4 text-sm text-gray-600 text-center">
                                  {lead.bio}
                                </div>
                              )}
                              
                              {/* View profile link */}
                              <div className="text-center mb-6">
                                <a
                                  href={`https://instagram.com/${lead.username}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors"
                                >
                                  <Instagram size={16} className="mr-2" /> View Instagram Profile
                                </a>
                              </div>
                              
                              {/* Accept/Decline buttons - Tinder style */}
                              <div className="flex items-center justify-center space-x-6">
                                <button
                                  onClick={() => handleDeclineLead(lead)}
                                  className="w-16 h-16 rounded-full bg-red-100 hover:bg-red-200 text-red-600 flex items-center justify-center transition-all hover:scale-110 shadow-lg"
                                  title="Decline"
                                >
                                  <X size={28} />
                                </button>
                                <button
                                  onClick={() => handleAcceptLead(lead)}
                                  className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-all hover:scale-110 shadow-lg"
                                  title="Accept"
                                >
                                  <Check size={28} />
                                </button>
                              </div>
                              
                              {/* Skip option */}
                              <div className="text-center mt-4">
                                <button
                                  onClick={() => setDiscoveryResults(prev => ({
                                    ...prev,
                                    leads: prev.leads.slice(1)
                                  }))}
                                  className="text-sm text-gray-400 hover:text-gray-600"
                                >
                                  Skip for now →
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                        
                        {/* Search again button */}
                        <button
                          onClick={() => { setDiscoveryResults(null); setSelectedLeads([]); }}
                          className="w-full py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                        >
                          <RefreshCw size={16} className="inline mr-2" />
                          Search Again
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <AlertCircle size={40} className="mx-auto mb-3 text-gray-400" />
                        <p className="text-lg font-medium">All done!</p>
                        <p className="text-sm mb-4">No more leads to review.</p>
                        <button
                          onClick={() => { setDiscoveryResults(null); setSelectedLeads([]); }}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                          <RefreshCw size={16} className="inline mr-2" />
                          Search Again
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Declined Leads Modal */}
      {showDeclinedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <X className="mr-2 text-red-500" size={20} />
                  Declined Leads ({declinedLeads.length})
                </h3>
                <button onClick={() => setShowDeclinedModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              
              <p className="text-sm text-gray-500 mb-4">
                These leads won't appear in future discovery searches. You can restore them if needed.
              </p>
              
              {declinedLeads.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No declined leads yet.</p>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {declinedLeads.map((lead) => (
                    <div key={lead.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <p className="font-medium text-gray-900">@{lead.username}</p>
                          {lead.follower_count && (
                            <span className="ml-2 text-xs text-gray-500">
                              {formatFollowers(lead.follower_count)} followers
                            </span>
                          )}
                        </div>
                        {lead.full_name && (
                          <p className="text-sm text-gray-500">{lead.full_name}</p>
                        )}
                        {lead.decline_reason && (
                          <p className="text-xs text-gray-400">Reason: {lead.decline_reason}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <a
                          href={`https://instagram.com/${lead.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-1 text-xs bg-pink-100 text-pink-700 rounded hover:bg-pink-200"
                        >
                          <Instagram size={12} className="inline mr-1" /> View
                        </a>
                        <button
                          onClick={() => handleRestoreLead(lead.id, lead.username)}
                          className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          <RefreshCw size={12} className="inline mr-1" /> Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <button
                onClick={() => setShowDeclinedModal(false)}
                className="w-full mt-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Prospects Modal - Tinder-style review */}
      {showProspectsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Users className="mr-2 text-amber-500" size={20} />
                  Prospects to Review
                </h3>
                <button onClick={() => setShowProspectsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              
              {/* Stats bar */}
              {prospectsStats && (
                <div className="grid grid-cols-4 gap-2 mb-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-amber-700">{prospectsStats.pending}</p>
                    <p className="text-xs text-amber-600">Pending</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-green-700">{prospectsStats.pending_with_email}</p>
                    <p className="text-xs text-green-600">With Email</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-blue-700">{prospectsStats.new_last_24h}</p>
                    <p className="text-xs text-blue-600">New Today</p>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-indigo-700">{prospectsStats.accepted}</p>
                    <p className="text-xs text-indigo-600">Accepted</p>
                  </div>
                </div>
              )}
              
              {/* Loading state */}
              {prospectsLoading ? (
                <div className="text-center py-12">
                  <Loader size={32} className="animate-spin mx-auto text-amber-500 mb-2" />
                  <p className="text-gray-500">Loading prospects...</p>
                </div>
              ) : prospects.length > 0 ? (
                /* Tinder-style card */
                <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
                  {/* Progress */}
                  <div className="text-center text-xs text-gray-400 mb-4">
                    Reviewing 1 of {prospects.length} prospects
                  </div>
                  
                  {/* Profile */}
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 mx-auto mb-3 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center">
                      <Instagram size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">@{prospects[0].username}</h3>
                    {prospects[0].full_name && (
                      <p className="text-gray-600 mt-1">{prospects[0].full_name}</p>
                    )}
                    {prospects[0].follower_count && (
                      <p className="text-lg font-semibold text-indigo-600 mt-1">
                        {formatFollowers(prospects[0].follower_count)} followers
                      </p>
                    )}
                    {prospects[0].email && (
                      <div className="mt-2 inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                        <Mail size={14} className="mr-1" /> {prospects[0].email}
                      </div>
                    )}
                    {prospects[0].niche && (
                      <p className="text-xs text-gray-400 mt-2">Found in: {prospects[0].niche}</p>
                    )}
                  </div>
                  
                  {/* Bio */}
                  {prospects[0].bio && (
                    <div className="bg-gray-100 rounded-lg p-3 mb-4 text-sm text-gray-600 text-center max-h-24 overflow-y-auto">
                      {prospects[0].bio}
                    </div>
                  )}
                  
                  {/* View profile link */}
                  <div className="text-center mb-6">
                    <a
                      href={`https://instagram.com/${prospects[0].username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors"
                    >
                      <Instagram size={16} className="mr-2" /> View Instagram Profile
                    </a>
                  </div>
                  
                  {/* Accept/Decline buttons */}
                  <div className="flex items-center justify-center space-x-6">
                    <button
                      onClick={() => handleDeclineProspect(prospects[0])}
                      className="w-16 h-16 rounded-full bg-red-100 hover:bg-red-200 text-red-600 flex items-center justify-center transition-all hover:scale-110 shadow-lg"
                      title="Decline"
                    >
                      <X size={28} />
                    </button>
                    <button
                      onClick={() => handleAcceptProspect(prospects[0])}
                      className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-all hover:scale-110 shadow-lg"
                      title="Accept"
                    >
                      <Check size={28} />
                    </button>
                  </div>
                  
                  {/* Skip option */}
                  <div className="text-center mt-4">
                    <button
                      onClick={() => setProspects(prev => [...prev.slice(1), prev[0]])}
                      className="text-sm text-gray-400 hover:text-gray-600"
                    >
                      Skip for now →
                    </button>
                  </div>
                </div>
              ) : (
                /* No prospects */
                <div className="text-center py-12 text-gray-500">
                  <AlertCircle size={40} className="mx-auto mb-3 text-gray-400" />
                  <p className="text-lg font-medium">No prospects to review</p>
                  <p className="text-sm mb-4">
                    Configure niches in Discovery Settings to start finding leads.
                  </p>
                  <button
                    onClick={() => { setShowProspectsModal(false); setShowNicheConfig(true); fetchNiches(); }}
                    className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600"
                  >
                    <Settings size={16} className="inline mr-2" /> Configure Niches
                  </button>
                </div>
              )}
              
              <button
                onClick={() => setShowProspectsModal(false)}
                className="w-full mt-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Niche Configuration Modal */}
      {showNicheConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Settings className="mr-2 text-gray-600" size={20} />
                  Discovery Settings
                </h3>
                <button onClick={() => setShowNicheConfig(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              
              <p className="text-sm text-gray-500 mb-4">
                Configure which niches to search for influencers. Discovery runs automatically every hour.
              </p>
              
              {/* Add new niche */}
              <div className="space-y-2 mb-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newNiche}
                    onChange={(e) => setNewNiche(e.target.value)}
                    placeholder="Enter a niche (e.g., instagram growth)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddNiche()}
                  />
                  <button
                    onClick={handleAddNiche}
                    disabled={!newNiche.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newNicheEmailOnly}
                    onChange={(e) => setNewNicheEmailOnly(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-600">
                    <Mail size={14} className="inline mr-1" />
                    Only find profiles with email in bio
                  </span>
                </label>
              </div>
              
              {/* Niche list */}
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                {configuredNiches.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <p>No niches configured yet.</p>
                    <p className="text-sm">Add niches above to start discovering leads.</p>
                  </div>
                ) : (
                  configuredNiches.map((niche) => (
                    <div key={niche.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-1">
                          <p className={`font-medium ${niche.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                            {niche.niche}
                          </p>
                          {niche.email_only && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded flex items-center">
                              <Mail size={10} className="mr-1" /> Email Only
                            </span>
                          )}
                          {!niche.is_active && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                              Paused
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">
                          {niche.total_leads_found} leads found
                          {niche.last_discovered_at && ` • Last: ${new Date(niche.last_discovered_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleToggleNiche(niche.id, niche.is_active)}
                          className={`px-2 py-1 text-xs rounded ${
                            niche.is_active 
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {niche.is_active ? 'Pause' : 'Resume'}
                        </button>
                        <button
                          onClick={() => handleDeleteNiche(niche.id)}
                          className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p className="font-medium">How it works:</p>
                <ul className="list-disc list-inside text-xs mt-1 space-y-1">
                  <li>Discovery runs every 15 minutes automatically</li>
                  <li>Maintains ~100 pending prospects at all times</li>
                  <li>Full profile details including follower counts are fetched</li>
                  <li>Adding a new niche triggers immediate discovery</li>
                </ul>
              </div>
              
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API_URL}/admin/influencers/discovery/run-now`, {}, getAuthHeaders());
                    toast.success('Discovery started! Check Prospects in a minute.');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to start discovery');
                  }
                }}
                className="w-full mt-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 flex items-center justify-center"
              >
                <Zap size={16} className="mr-2" /> Run Discovery Now
              </button>
              
              <button
                onClick={() => setShowNicheConfig(false)}
                className="w-full mt-2 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Preview Modal */}
      {showEmailPreview && previewEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold flex items-center">
                    <Mail size={20} className="mr-2" />
                    Email Preview
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">{previewEmail.subject}</p>
                </div>
                <button
                  onClick={() => { setShowEmailPreview(false); setPreviewEmail(null); }}
                  className="text-white hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            {/* Email metadata */}
            <div className="px-6 py-3 bg-gray-50 border-b text-sm">
              <div className="flex flex-wrap gap-4">
                <span className="text-gray-600">
                  <strong>Sent:</strong> {new Date(previewEmail.sent_at).toLocaleString()}
                </span>
                <span className={previewEmail.email_opened ? 'text-green-600' : 'text-gray-400'}>
                  {previewEmail.email_opened ? '✓ Opened' : 'Not opened'}
                  {previewEmail.email_opened_at && ` (${new Date(previewEmail.email_opened_at).toLocaleString()})`}
                </span>
                <span className={previewEmail.email_clicked ? 'text-green-600' : 'text-gray-400'}>
                  {previewEmail.email_clicked ? '✓ Clicked' : 'No clicks'}
                  {previewEmail.email_clicked_at && ` (${new Date(previewEmail.email_clicked_at).toLocaleString()})`}
                </span>
              </div>
            </div>
            
            {/* Email content - rendered in iframe for safety */}
            <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
              <div className="border rounded-lg bg-white shadow-inner">
                <iframe
                  srcDoc={previewEmail.content || '<p style="color: #999; padding: 20px;">No content available</p>'}
                  title="Email Preview"
                  className="w-full border-0"
                  style={{ minHeight: '400px' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-3 bg-gray-50 border-t flex justify-end">
              <button
                onClick={() => { setShowEmailPreview(false); setPreviewEmail(null); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
/* eslint-enable no-unused-vars */
